<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import CollectionsHoldAlert from '@/components/shared/CollectionsHoldAlert';
    import utilsService from '@/services/core/utils.service';
    import httpService from '@/services/core/http.service';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import WellDBOperatorsAutocomplete from './WellDBOperatorsAutocomplete';
    import GsbSelect from '@/components/shared/GsbSelect';
    import InfoTooltip from '@/components/shared/InfoTooltip';
    import store from '@/store/global.store';
    import companiesService from '@/services/companies.service';

    export default {
        props: {
            modalData: Object
        },
        components: {
            CollectionsHoldAlert,
            Modal,
            ButtonSpinner,
            WellDBOperatorsAutocomplete,
            GsbSelect,
            InfoTooltip
        },
        data() {
            return {
                isLoading: 0,
                tabs: ['General', 'API Keys'],
                selectedTab: 'General',
                maxApiKeys: 4,
                apiKeys: [],
                newApiKey: { name: '', readOnly: true },
                pendingApiKeyAction: null,
                savedApiSettings: null,
                company: {
                    allowBulkUploadData: true,
                    isAllocationsEnabled: true,
                    isScadaAgreementSigned: false,
                    isAPIWriteAgreementSigned: false,
                    isCopumperEnabled: false,
                    cliEnabled: false,
                    allowSelfServiceApiKeys: false,
                    copumperMonthlyCostLimitUsd: null,
                    passwordExpirationDays: 182,
                    user: {
                        username: '',
                        email: '',
                        fullName: ''
                    }
                },
                measurementOptions: {},
                subscriptionOptions: [
                    { id: 1, name: 'Well based' },
                    { id: 2, name: 'Pumper based' }
                ],
                countryOptions: [
                    { id: 'US', name: 'United States' },
                    { id: 'CA', name: 'Canada' }
                ],
                apiAccessOptions: [
                    { id: '', name: 'Choose access…' },
                    { id: true, name: 'Read only' },
                    { id: false, name: 'Read & write' }
                ],
                licenseOptions: [
                    { id: '', name: 'None' },
                    { id: false, name: 'Must Sign' },
                    { id: true, name: 'Signed' }
                ]
            };
        },
        computed: {
            isReadOnly() {
                return store.getters.currentUser()?.isReadOnly === true;
            },
            isNew() {
                const { item = {} } = this.modalData || {};
                return item.id == 0;
            },
            gasPrecisionOptions() {
                const { gas } = this.company;
                const { gasOptions = [] } = this.measurementOptions;

                switch (+gas) {
                    case 1: //ThousandsOfCubicFeet
                        return gasOptions.filter(f => [1, 2].indexOf(f.id) >= 0);
                    case 2: //MillionCubicFeet
                        return gasOptions.filter(f => f.id == 2);
                    case 3: //ThousandCubicMeters
                        return gasOptions.filter(f => [3, 4].indexOf(f.id) >= 0);
                    case 4: //MillionCubicMeters
                        return gasOptions.filter(f => f.id == 4);
                    default:
                        return null;
                }
            }
        },
        methods: {
            close() {
                modalService.closeModal();
            },
            save() {
                if (this.isReadOnly) return;

                if (this.isApiAccessMissing()) {
                    this.selectedTab = 'API Keys';
                    utilsService.showToast('Choose Read only or Read & write for the Public API access', 'error');
                    return;
                }

                if (this.company.publicApiReadonly === '') {
                    this.company.publicApiReadonly = false;
                }

                const url = `companies`;
                const companyDto = this._mapTo(this.company);

                this.isLoading++;
                httpService
                    .post(url, companyDto)
                    .then(() => {
                        utilsService.showToast('Company was saved');
                        this.modalData.onSuccess && this.modalData.onSuccess();
                        this.close();
                    })
                    .finally(() => this.isLoading--);
            },
            loadApiKeys() {
                companiesService.listApiKeys(this.company.id).then(keys => {
                    this.apiKeys = keys;
                });
            },
            createApiKey() {
                const readOnly = this.newApiKey.readOnly || this.company.publicApiReadonly !== false;
                companiesService.createApiKey(this.company.id, { name: this.newApiKey.name, readOnly }).then(() => {
                    this.newApiKey = { name: '', readOnly: true };
                    utilsService.showToast('API key created');
                    this.loadApiKeys();
                });
            },
            copyApiKey(key) {
                navigator.clipboard.writeText(key.key).then(() => utilsService.showToast('API key copied'));
            },
            askApiKeyAction(key, action) {
                this.pendingApiKeyAction = { id: key.id, action };
            },
            isPendingApiKeyAction(key, action) {
                return this.pendingApiKeyAction?.id === key.id && this.pendingApiKeyAction?.action === action;
            },
            confirmApiKeyAction(key) {
                const { action } = this.pendingApiKeyAction;
                this.pendingApiKeyAction = null;
                if (action === 'rotate') this.rotateApiKey(key);
                else this.deleteApiKey(key);
            },
            rotateApiKey(key) {
                companiesService.rotateApiKey(this.company.id, key.id).then(rotated => {
                    if (key.isLegacy) this.company.secretKey = rotated.key;
                    utilsService.showToast('API key rotated');
                    this.loadApiKeys();
                });
            },
            deleteApiKey(key) {
                companiesService.deleteApiKey(this.company.id, key.id).then(() => {
                    if (key.isLegacy) this.company.secretKey = null;
                    utilsService.showToast('API key deleted');
                    this.loadApiKeys();
                });
            },
            loadMeasurementOptions() {
                const url = `companies/measurement-options`;
                httpService.get(url).then(data => {
                    this.measurementOptions = data;
                });
            },
            loadCountry(countryCode) {
                switch (countryCode) {
                    case 'US':
                        this.company.gas = 1;
                        this.company.gasPrecision = 1;
                        this.company.length = 1;
                        this.company.fluids = 1;
                        this.company.ngl = 2;
                        this.company.pressure = 1;
                        this.company.temperature = 1;
                        break;
                    case 'CA':
                        this.company.gas = 3;
                        this.company.gasPrecision = 3;
                        this.company.length = 4;
                        this.company.fluids = 4;
                        this.company.ngl = 5;
                        this.company.pressure = 3;
                        this.company.temperature = 2;
                        break;
                }
            },
            advancedWaterOptionsChange() {
                this.company.useCustomWaterProducedFormula = this.company.advancedWaterOptions;
            },
            apiSettingsOf(company) {
                return {
                    publicApiEnabled: company.publicApiEnabled === true,
                    publicApiReadonly: company.publicApiReadonly,
                    allowSelfServiceApiKeys: company.allowSelfServiceApiKeys === true
                };
            },
            hasUnsavedApiSettings() {
                return JSON.stringify(this.apiSettingsOf(this.company)) !== JSON.stringify(this.savedApiSettings);
            },
            isKeyReadOnly(key) {
                return this.company.publicApiReadonly === true || key.readOnly;
            },
            onPublicApiEnabledChange() {
                if (this.company.publicApiEnabled && !this.savedApiSettings.publicApiEnabled) {
                    this.company.publicApiReadonly = '';
                }
            },
            isApiAccessMissing() {
                return this.company.publicApiEnabled && this.company.publicApiReadonly === '';
            },
            enableTinyPumperChange() {
                this.company.allowFullAlerts = this.company.enableTinyPumper ? this.company.enableTinyPumper : this.company.allowFullAlerts;
                this.company.enableOilAutoTickets = this.company.enableTinyPumper
                    ? this.company.enableTinyPumper
                    : this.company.enableOilAutoTickets;
                this.company.enableWaterAutoTickets = this.company.enableTinyPumper
                    ? this.company.enableTinyPumper
                    : this.company.enableWaterAutoTickets;
            },
            isCopumperEnabledChange() {
                // The monthly cost limit only makes sense when Copumper is enabled.
                if (!this.company.isCopumperEnabled) this.company.copumperMonthlyCostLimitUsd = null;
            },
            _mapTo(company) {
                let isScadaAgreementSigned = null;
                let isAPIWriteAgreementSigned = null;

                if (company.enableTinyPumper && company.isScadaAgreementSigned !== '')
                    isScadaAgreementSigned = company.isScadaAgreementSigned;

                if (company.publicApiEnabled && !company.publicApiReadonly && company.isAPIWriteAgreementSigned !== '')
                    isAPIWriteAgreementSigned = company.isAPIWriteAgreementSigned;

                // Ensure user property is always present, even for updates
                const user = company.user || {
                    username: '',
                    email: '',
                    fullName: ''
                };

                return {
                    ...company,
                    user,
                    isScadaAgreementSigned,
                    isAPIWriteAgreementSigned
                };
            }
        },
        mounted() {
            const company = this.isNew ? { ...this.modalData.item, ...this.company } : this.modalData.item;

            this.company = utilsService.toJS(company);
            this.savedApiSettings = this.apiSettingsOf(this.company);

            // Ensure user property exists for both new and existing companies
            if (!this.company.user) {
                this.company.user = {
                    username: '',
                    email: '',
                    fullName: ''
                };
            }

            this.loadMeasurementOptions();

            if (!this.isNew) {
                this.loadApiKeys();
            }

            if (this.company.id == 0) {
                this.loadCountry(this.company.country);
            }
        }
    };
</script>

<template>
    <Modal sizeClass="modal-xl">
        <form class="gsb-form" @submit.prevent="save">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">Company</h3>
                </div>
                <div class="modal-body" :class="{ 'gsb-readonly': isReadOnly }">
                    <div v-if="!isNew && !company.paidFeaturesEnabled" class="mb-3">
                        <CollectionsHoldAlert :hold="company.collectionsHold" />
                        <!-- One way: A/R turns this off; this portal can only turn it back on. -->
                        <div class="form-check form-switch">
                            <input
                                id="paidFeaturesEnabled"
                                class="form-check-input"
                                type="checkbox"
                                role="switch"
                                :checked="company.paidFeaturesEnabled"
                                :disabled="isReadOnly"
                                @change="company.paidFeaturesEnabled = true"
                            />
                            <label class="form-check-label" for="paidFeaturesEnabled">Paid features enabled</label>
                        </div>
                    </div>
                    <ul class="nav nav-tabs mb-3">
                        <li class="nav-item" v-for="tab in tabs" :key="tab" @click="selectedTab = tab">
                            <a class="nav-link" :class="{ active: tab === selectedTab }" href="javascript:;">{{ tab }}</a>
                        </li>
                    </ul>

                    <div v-show="selectedTab === 'General'" class="row">
                        <div class="col-lg-6">
                            <div>
                                <fieldset>
                                    <b class="fieldset-title">Company Data</b>
                                    <div class="row mb-2">
                                        <label class="col-sm-3 col-form-label">Name</label>
                                        <div class="col-sm">
                                            <input class="form-control" type="text" required v-model="company.name" />
                                        </div>
                                    </div>

                                    <div class="row mb-2">
                                        <label class="col-sm-3 col-form-label lh-1">Subscription Type</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.subscriptionType" :options="subscriptionOptions" />
                                        </div>
                                    </div>

                                    <div class="row">
                                        <label class="col-sm-3 col-form-label">Contract Company</label>
                                        <div class="col-sm">
                                            <GsbSelect
                                                :modelValue="company.contractCompany || false"
                                                @change="company.contractCompany = $event"
                                                required
                                                style="max-width: 100px"
                                                :options="[
                                                    { id: false, name: 'No' },
                                                    { id: true, name: 'Yes' }
                                                ]"
                                            />
                                        </div>
                                    </div>
                                    <div v-if="isNew" class="row mt-2">
                                        <label class="col-sm-3 col-form-label lh-1">WellDB Operator</label>
                                        <div class="col-sm">
                                            <WellDBOperatorsAutocomplete v-model:company="company" />
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset v-if="isNew && company.user">
                                    <b class="fieldset-title">Internal User</b>
                                    <div class="row mb-2">
                                        <label class="col-sm-3 col-form-label">Username</label>
                                        <div class="col-sm">
                                            <input type="text" v-model="company.user.username" class="form-control" required />
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <label class="col-sm-3 col-form-label">Full Name</label>
                                        <div class="col-sm">
                                            <input type="text" v-model="company.user.fullName" class="form-control" required />
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <label class="col-sm-3 col-form-label">Email</label>
                                        <div class="col-sm">
                                            <input type="text" v-model="company.user.email" class="form-control" />
                                        </div>
                                    </div>
                                </fieldset>
                                <fieldset>
                                    <b class="fieldset-title">Internationalization</b>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">Country</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.country" @change="loadCountry" :options="countryOptions" />
                                        </div>
                                    </div>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">Gas</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.gas" :options="measurementOptions.gasOptions" />
                                        </div>
                                    </div>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">Gas Precision</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.gasPrecision" :options="gasPrecisionOptions" />
                                        </div>
                                    </div>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">Length</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.length" :options="measurementOptions.lenOptions" />
                                        </div>
                                    </div>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">Volume</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.fluids" :options="measurementOptions.volOptions" />
                                        </div>
                                    </div>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">NGL Volume</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.ngl" :options="measurementOptions.volOptions" />
                                        </div>
                                    </div>
                                    <div class="mb-2 row">
                                        <label class="col-sm-3 col-form-label">Pressure</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.pressure" :options="measurementOptions.psiOptions" />
                                        </div>
                                    </div>
                                    <div class="row">
                                        <label class="col-sm-3 col-form-label">Temperature</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.temperature" :options="measurementOptions.tempOptions" />
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                        <div class="col-lg-6 ps-4">
                            <div>
                                <fieldset>
                                    <b class="fieldset-title">Configurations</b>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            v-model="company.allowBulkUploadData"
                                            id="allowBulkUploadData"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="allowBulkUploadData">Allow bulk upload data</label>
                                    </div>

                                    <div class="form-check">
                                        <input class="form-check-input" v-model="company.allowBoltOns" id="allowBoltOns" type="checkbox" />
                                        <label class="form-check-label" for="allowBoltOns">Allow Bolt ons</label>
                                        <small>Attachments + Records</small>
                                    </div>

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            v-model="company.allowUserTracking"
                                            id="allowUserTracking"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="allowUserTracking">Allow User Tracking</label>
                                    </div>

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            v-model="company.showForcedAllocations"
                                            id="showForcedAllocations"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="showForcedAllocations">Show forced tests</label>
                                    </div>

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            v-model="company.allowFullAlerts"
                                            id="allowFullAlerts"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="allowFullAlerts">Allow full alerts</label>
                                    </div>

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            v-model="company.advancedWaterOptions"
                                            id="advancedWaterOptions"
                                            type="checkbox"
                                            @change="advancedWaterOptionsChange"
                                        />
                                        <label class="form-check-label" for="advancedWaterOptions">Show Advanced Water Options</label>
                                    </div>

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            :disabled="company.advancedWaterOptions"
                                            v-model="company.useCustomWaterProducedFormula"
                                            id="useCustomWaterProducedFormula"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="useCustomWaterProducedFormula"
                                            >Use custom Water Produced formula</label
                                        >
                                    </div>

                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="mergeWellsAndAllocations"
                                            v-model="company.mergeWellsAndAllocations"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="mergeWellsAndAllocations"
                                            >Merge well measurements and allocations tab</label
                                        >
                                    </div>
                                    <div class="d-flex align-items-center mt-1">
                                        <input
                                            type="number"
                                            v-model="company.maxBatteriesPerUserAllowed"
                                            class="form-control form-control-sm"
                                            required
                                            style="width: 60px; height: 25px"
                                        />
                                        <label class="ms-1">Max Batteries Per User Allowed</label>
                                    </div>
                                    <div class="d-flex align-items-center mt-1">
                                        <input
                                            type="number"
                                            v-model="company.passwordExpirationDays"
                                            class="form-control form-control-sm"
                                            required
                                            style="width: 60px; height: 25px"
                                        />
                                        <label class="ms-1">Days for Password Expiration</label>
                                    </div>
                                </fieldset>
                                <fieldset>
                                    <b class="fieldset-title">Reports</b>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="isAllocationsEnabled"
                                            v-model="company.isAllocationsEnabled"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="isAllocationsEnabled">Allocation enabled</label>
                                        <small class="ms-1">(Downtime reasons + Well comments + Allow well performance)</small>
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <b class="fieldset-title">Integrations</b>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="allowSensaphone"
                                            v-model="company.allowSensaphone"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="allowSensaphone">Allocation Sensaphone</label>
                                    </div>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="enableTinyPumper"
                                            v-model="company.enableTinyPumper"
                                            type="checkbox"
                                            @change="enableTinyPumperChange"
                                        />
                                        <label class="form-check-label" for="enableTinyPumper">Enable Tiny Pumper</label>
                                    </div>
                                </fieldset>

                                <fieldset v-if="company.enableTinyPumper">
                                    <b class="fieldset-title">Licenses</b>

                                    <div v-if="company.enableTinyPumper" class="row mb-2">
                                        <label class="col-sm-4 col-form-label">SCADA License Status</label>
                                        <div class="col-sm">
                                            <GsbSelect v-model="company.isScadaAgreementSigned" :options="licenseOptions" />
                                        </div>
                                    </div>

                                </fieldset>

                                <fieldset>
                                    <b class="fieldset-title">Tickets</b>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="enableOilAutoTickets"
                                            v-model="company.enableOilAutoTickets"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="enableOilAutoTickets">Enable Oil Auto Tickets</label>
                                    </div>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="enableWaterAutoTickets"
                                            v-model="company.enableWaterAutoTickets"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="enableWaterAutoTickets">Enable Water Auto Tickets</label>
                                    </div>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="enableInboundTickets"
                                            v-model="company.enableInboundTickets"
                                            type="checkbox"
                                        />
                                        <label class="form-check-label" for="enableInboundTickets">Enable Inbound Tickets</label>
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <b class="fieldset-title">Greasebot (AI)</b>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="isCopumperEnabled"
                                            v-model="company.isCopumperEnabled"
                                            type="checkbox"
                                            @change="isCopumperEnabledChange"
                                        />
                                        <label class="form-check-label" for="isCopumperEnabled">Enable Greasebot</label>
                                    </div>
                                    <div v-if="company.isCopumperEnabled" class="d-flex align-items-center mt-1">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            v-model.number="company.copumperMonthlyCostLimitUsd"
                                            class="form-control form-control-sm"
                                            placeholder="Unlimited"
                                            style="width: 100px; height: 25px"
                                        />
                                        <label class="ms-1">Monthly Cost Limit (USD) — leave blank for unlimited</label>
                                    </div>
                                </fieldset>

                                <fieldset>
                                    <b class="fieldset-title">GreaseBook CLI</b>
                                    <div class="form-check">
                                        <input class="form-check-input" id="cliEnabled" v-model="company.cliEnabled" type="checkbox" />
                                        <label class="form-check-label" for="cliEnabled">Enable CLI (users create a token in their name menu)</label>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>

                    <div v-show="selectedTab === 'API Keys'">
                        <div class="row">
                            <div class="col-lg-6">
                                <fieldset>
                                    <b class="fieldset-title">Public API</b>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            id="publicApiEnabled"
                                            v-model="company.publicApiEnabled"
                                            type="checkbox"
                                            @change="onPublicApiEnabledChange"
                                        />
                                        <label class="form-check-label" for="publicApiEnabled">Enable Public API</label>
                                        <InfoTooltip iconClass="bi bi-info-circle text-muted ms-1" text="Enabling it issues the primary key, listed under Keys below." />
                                    </div>
                                    <template v-if="company.publicApiEnabled">
                                        <div class="row mt-2 mb-2">
                                            <label class="col-sm-4 col-form-label">
                                                Access
                                                <InfoTooltip
                                                    iconClass="bi bi-info-circle text-muted ms-1"
                                                    text="What the company pays for. Read only makes every key read only. Moving to Read & write later keeps existing keys as they are; new keys can be write."
                                                />
                                            </label>
                                            <div class="col-sm">
                                                <GsbSelect
                                                    v-model="company.publicApiReadonly"
                                                    :options="apiAccessOptions"
                                                    :class="{ 'is-invalid': isApiAccessMissing() }"
                                                />
                                                <div v-if="isApiAccessMissing()" class="invalid-feedback d-block">
                                                    Choose what the company pays for before saving.
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="company.publicApiReadonly === false" class="row mb-2">
                                            <label class="col-sm-4 col-form-label">Write License</label>
                                            <div class="col-sm">
                                                <GsbSelect v-model="company.isAPIWriteAgreementSigned" :options="licenseOptions" />
                                            </div>
                                        </div>
                                        <div class="form-check">
                                            <input class="form-check-input" id="publicApiRequestLimit" v-model="company.publicApiRequestLimit" type="checkbox" />
                                            <label class="form-check-label" for="publicApiRequestLimit">Public API request limit</label>
                                        </div>
                                        <div class="form-check">
                                            <input class="form-check-input" id="allowSelfServiceApiKeys" v-model="company.allowSelfServiceApiKeys" type="checkbox" />
                                            <label class="form-check-label" for="allowSelfServiceApiKeys">Allow self-service API keys</label>
                                            <InfoTooltip
                                                iconClass="bi bi-info-circle text-muted ms-1"
                                                text="Execs can create, rotate and delete up to 4 keys from the app, within the access above."
                                            />
                                        </div>
                                    </template>
                                </fieldset>
                            </div>
                            <div class="col-lg-6">
                                <fieldset v-if="!isNew && company.publicApiEnabled">
                                    <b class="fieldset-title">Public API Usage</b>
                                    <div class="row mb-1">
                                        <label class="col-sm-4 col-form-label">Last Seen (UTC)</label>
                                        <div class="col-sm">
                                            <span class="form-control-plaintext">
                                                {{ company.lastPublicApiCallUtc || 'Never' }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="company.lastPublicApiEndpoint" class="row mb-1">
                                        <label class="col-sm-4 col-form-label">Last Endpoint</label>
                                        <div class="col-sm">
                                            <span class="form-control-plaintext">{{ company.lastPublicApiEndpoint }}</span>
                                        </div>
                                    </div>
                                    <div class="row mb-1">
                                        <label class="col-sm-4 col-form-label">Calls Today</label>
                                        <div class="col-sm">
                                            <span class="form-control-plaintext">{{ company.callsToday || 0 }}</span>
                                        </div>
                                    </div>
                                    <div class="row mb-1">
                                        <label class="col-sm-4 col-form-label">Calls (last 30 days)</label>
                                        <div class="col-sm">
                                            <span class="form-control-plaintext">{{ company.calls30Days || 0 }}</span>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>

                        <fieldset v-if="!isNew && company.publicApiEnabled">
                            <b class="fieldset-title">Keys</b>
                            <div v-if="hasUnsavedApiSettings()" class="alert alert-warning py-2 small">
                                Save the company to apply the Public API changes above. Keys can be created, rotated or deleted after that.
                            </div>
                            <div class="table-responsive">
                            <table class="table table-sm align-middle mb-2">
                                <thead class="small text-muted">
                                    <tr>
                                        <th class="fw-normal">Name</th>
                                        <th class="fw-normal">Key</th>
                                        <th class="fw-normal">Access</th>
                                        <th class="fw-normal">Created</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="key in apiKeys" :key="key.id">
                                        <td class="text-nowrap">
                                            {{ key.name }}
                                            <small v-if="key.isLegacy" class="text-muted ms-1">primary</small>
                                        </td>
                                        <td class="text-nowrap">
                                            <span class="text-muted small font-monospace user-select-none">
                                                <span>{{ key.key.slice(0, -8) }}</span><span class="text-blur">{{ key.key.slice(-8) }}</span>
                                            </span>
                                            <button type="button" class="btn btn-link btn-sm py-0" title="Copy key" @click="copyApiKey(key)">Copy</button>
                                        </td>
                                        <td>
                                            <span class="badge rounded-pill" :class="isKeyReadOnly(key) ? 'bg-light text-dark border' : 'bg-primary'">
                                                {{ isKeyReadOnly(key) ? 'Read only' : 'Read / write' }}
                                            </span>
                                        </td>
                                        <td class="text-muted small text-nowrap">{{ key.createdDate ? key.createdDate.substring(0, 10) : '' }}</td>
                                        <td v-if="!isReadOnly && !hasUnsavedApiSettings()" class="text-end text-nowrap">
                                            <template v-if="pendingApiKeyAction && pendingApiKeyAction.id === key.id">
                                                <small class="text-muted">{{ isPendingApiKeyAction(key, 'rotate') ? 'Rotate?' : 'Delete?' }}</small>
                                                <button type="button" class="btn btn-link btn-sm text-danger py-0" @click="confirmApiKeyAction(key)">Confirm</button>
                                                <button type="button" class="btn btn-link btn-sm text-secondary py-0" @click="pendingApiKeyAction = null">Cancel</button>
                                            </template>
                                            <template v-else>
                                                <button type="button" class="btn btn-link btn-sm text-secondary py-0" @click="askApiKeyAction(key, 'rotate')">Rotate</button>
                                                <button type="button" class="btn btn-link btn-sm text-danger py-0" @click="askApiKeyAction(key, 'delete')">Delete</button>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="apiKeys.length === 0">
                                        <td colspan="5" class="text-muted">No keys. Create one below, or save the company to issue a new primary key.</td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                            <div v-if="!isReadOnly && !hasUnsavedApiSettings() && apiKeys.length < maxApiKeys" class="d-flex align-items-center gap-3 mb-2">
                                <input class="form-control form-control-sm" style="max-width: 280px" type="text" maxlength="100" placeholder="Key name (optional)" v-model="newApiKey.name" />
                                <div v-if="company.publicApiReadonly === false" class="form-check mb-0">
                                    <input class="form-check-input" id="newApiKeyReadOnly" type="checkbox" v-model="newApiKey.readOnly" />
                                    <label class="form-check-label" for="newApiKeyReadOnly">Read only</label>
                                </div>
                                <button type="button" class="btn btn-outline-primary btn-sm" @click="createApiKey">Create key</button>
                            </div>
                            <small class="text-muted">A company holds at most {{ maxApiKeys }} keys. A rotated or deleted key stops working on the Public API within 10 minutes.</small>
                        </fieldset>
                        <p v-else-if="isNew" class="text-muted">Save the company first. Enabling the Public API issues the primary key.</p>
                    </div>
                </div>

                <div class="modal-footer d-flex justify-content-between">
                    <button type="button" class="btn" @click="close">Close</button>
                    <ButtonSpinner v-if="!isReadOnly" :isLoading="isLoading" />
                </div>
            </div>
        </form>
    </Modal>
</template>

